// Port of Services/SBotAIConfigService.swift (read side) + the personality
// pieces of Models/SBotAIConfigModels.swift.
//
// The web reads personalities from the shared `sbotAIConfig/personalityConfig`
// doc (signed-in readable; SBot Labs admins write it from iOS). The
// `providerCredentials` doc is deliberately ignored: its encrypted keys are the
// iOS key-distribution channel — on the web, the Functions proxy holds keys.

import { doc, onSnapshot, type DocumentData, type Unsubscribe } from "firebase/firestore";
import {
  AIProvider,
  AnthropicSettings,
  GeminiSettings,
  OpenAISettings,
  type ModelSettings,
} from "axon-provider-kit/core";
import { db } from "@/firebase/app";
import {
  EFFORT_TO_OPENAI_REASONING,
  type SBotChatConversation,
  type SBotEffortLevel,
} from "@/models/aiChat";
import { trimmedNonEmpty } from "@/models/firestore";
import type { SBotUser } from "@/models/user";

// Mirrors SBotAIPersonalityConfig.defaultSystemPrompt.
export const DEFAULT_SYSTEM_PROMPT =
  "You are SBot, a concise, helpful AI assistant for sports, publishing, research, and operations work. " +
  "When SBot League tools are available, derive identity and permissions from the logged-in user only. " +
  "Prefer read tools before prepare tools. Discover broadcast overlays and their input handles before preparing overlay changes, " +
  "use the returned mutable-field guide, and request explicit final IN or OUT states instead of toggles. Never claim a mutating action has happened until " +
  "execute_approved_sbot_league_action returns successfully after user approval.";

export const APPROVAL_COMPLETION_GUIDANCE =
  "\nAfter a user decides an approval, use the recorded execute_approved_sbot_league_action result as execution evidence. " +
  "Report successful changes accurately. On failure, diagnose the returned error and, if repair is appropriate, prepare a fresh action for user approval. " +
  "Never automatically retry a mutation or reuse a consumed approval. If the outcome is unknown, use read tools to establish whether it succeeded before preparing a replacement. " +
  "A rejected action must remain unexecuted unless the user requests a new action.";

export interface SBotAIPersonalityModelSettings {
  temperature?: number;
  topP?: number;
  topK?: number;
  frequencyPenalty?: number;
  presencePenalty?: number;
  stopSequences?: string[];
  maxTokens?: number;
  maxToolRounds?: number;
}

export interface SBotAIPersonalizationFields {
  email: boolean;
  displayName: boolean;
  firstName: boolean;
  lastName: boolean;
  bio: boolean;
  activeWorkspace: boolean;
}

export interface SBotAIPersonalizationConfig {
  isEnabled: boolean;
  enabledFields: SBotAIPersonalizationFields;
  template?: string;
}

export interface SBotAIAudioModeConfig {
  isEnabled: boolean;
  providerID: string;
  modelID: string;
  voiceID: string;
}

export interface SBotAIPersonality {
  id: string;
  name: string;
  description?: string;
  avatarURL?: string;
  systemPrompt: string;
  providerID: string;
  modelID: string;
  modelSettings: SBotAIPersonalityModelSettings;
  personalizationConfig?: SBotAIPersonalizationConfig;
  audioModeConfig?: SBotAIAudioModeConfig;
  isEnabled: boolean;
  isDefault: boolean;
  sortOrder: number;
}

/** Built-in fallback when no personalities are configured (mirrors iOS default). */
export const FALLBACK_PERSONALITY: SBotAIPersonality = {
  id: "sbot-default",
  name: "SBot",
  systemPrompt: DEFAULT_SYSTEM_PROMPT,
  providerID: "openai",
  modelID: "gpt-5.5",
  // No sampling overrides: reasoning models (gpt-5.x) reject non-default temperature.
  modelSettings: {},
  isEnabled: true,
  isDefault: true,
  sortOrder: 0,
};

const WEB_SUPPORTED_AI_PROVIDER_IDS: ReadonlySet<string> = new Set([
  AIProvider.openai,
  AIProvider.anthropic,
  AIProvider.gemini,
  AIProvider.xai,
]);

export function isWebAIProviderSupported(providerID: string): providerID is AIProvider {
  return WEB_SUPPORTED_AI_PROVIDER_IDS.has(providerID);
}

export function listenPersonalities(onChange: (personalities: SBotAIPersonality[]) => void): Unsubscribe {
  const reference = doc(db, "sbotAIConfig", "personalityConfig");
  return onSnapshot(
    reference,
    (snapshot) => {
      const data = snapshot.data();
      onChange(data ? parsePersonalities(data) : []);
    },
    (error) => {
      console.warn("aiConfigService personality listener failed:", error);
      onChange([]);
    },
  );
}

export function parsePersonalities(documentData: DocumentData): SBotAIPersonality[] {
  const map = documentData.personalities;
  if (typeof map !== "object" || map === null) return [];
  const personalities: SBotAIPersonality[] = [];
  for (const [id, value] of Object.entries(map as Record<string, unknown>)) {
    const personality = parsePersonality(id, value);
    if (personality) personalities.push(personality);
  }
  return personalities.sort(personalitySort);
}

export function enabledPersonalities(personalities: SBotAIPersonality[]): SBotAIPersonality[] {
  return personalities.filter((personality) => personality.isEnabled).sort(personalitySort);
}

/**
 * Personalities the browser can execute through the Functions proxy.
 * The fallback keeps chat usable when the shared config contains only
 * Apple/local personalities or is unavailable.
 */
export function webEnabledPersonalities(personalities: SBotAIPersonality[]): SBotAIPersonality[] {
  const supported = enabledPersonalities(personalities).filter((personality) =>
    isWebAIProviderSupported(personality.providerID),
  );
  return supported.length > 0 ? supported : [FALLBACK_PERSONALITY];
}

export function defaultPersonality(personalities: SBotAIPersonality[]): SBotAIPersonality {
  const enabled = enabledPersonalities(personalities);
  return enabled.find((personality) => personality.isDefault) ?? enabled[0] ?? FALLBACK_PERSONALITY;
}

export function defaultWebPersonality(personalities: SBotAIPersonality[]): SBotAIPersonality {
  const enabled = webEnabledPersonalities(personalities);
  return enabled.find((personality) => personality.isDefault) ?? enabled[0] ?? FALLBACK_PERSONALITY;
}

/**
 * Rebase only the active selection of a conversation whose personality cannot
 * run on web. Historical messages and their provider/personality metadata are
 * preserved verbatim.
 */
export function normalizeWebConversationSelection(
  personalities: SBotAIPersonality[],
  conversation: SBotChatConversation,
): SBotChatConversation {
  const configuredSelection = enabledPersonalities(personalities).find(
    (personality) =>
      personality.id === conversation.selectedPersonalityID
      && isWebAIProviderSupported(personality.providerID),
  );
  if (configuredSelection) {
    return conversation;
  }

  const fallback = defaultWebPersonality(personalities);
  if (
    conversation.selectedPersonalityID === fallback.id
    && conversation.selectedModelID === fallback.modelID
  ) {
    return conversation;
  }
  return {
    ...conversation,
    selectedPersonalityID: fallback.id,
    selectedModelID: fallback.modelID,
  };
}

/** Mirrors SBotAIConfigService.buildPersonalizedSystemPrompt. */
export function buildPersonalizedSystemPrompt(personality: SBotAIPersonality, user: SBotUser | null | undefined): string {
  const config = personality.personalizationConfig;
  if (!user || !config?.isEnabled) return personality.systemPrompt;

  const context = personalizationContext(user, config.enabledFields);
  if (Object.keys(context).length === 0) return personality.systemPrompt;

  const template = trimmedNonEmpty(config.template);
  if (template) {
    let rendered = template;
    for (const [key, value] of Object.entries(context)) {
      rendered = rendered.split(`{{${key}}}`).join(value);
    }
    return `${personality.systemPrompt}\n\n--- User Context ---\n${rendered}`;
  }

  const lines = Object.entries(context)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `- ${key}: ${value}`)
    .join("\n");
  return `${personality.systemPrompt}\n\n--- User Context ---\n${lines}`;
}

/** Mirrors SBotAIPersonalityModelSettings.modelSettings(for:effort:). */
export function providerModelSettings(personality: SBotAIPersonality, effort: SBotEffortLevel): ModelSettings | undefined {
  const settings = personality.modelSettings;
  switch (personality.providerID) {
    case AIProvider.anthropic:
      return new AnthropicSettings({
        ...(settings.temperature !== undefined && { temperature: clamp(settings.temperature, 0, 1) }),
        ...(settings.topP !== undefined && { topP: settings.topP }),
        ...(settings.topK !== undefined && { topK: settings.topK }),
      });
    case AIProvider.gemini:
      return new GeminiSettings({
        ...(settings.temperature !== undefined && { temperature: settings.temperature }),
        ...(settings.topP !== undefined && { topP: settings.topP }),
        ...(settings.topK !== undefined && { topK: settings.topK }),
        ...(settings.stopSequences !== undefined && { stopSequences: settings.stopSequences }),
      });
    case AIProvider.openai:
      return new OpenAISettings({
        ...(settings.temperature !== undefined && { temperature: settings.temperature }),
        ...(settings.topP !== undefined && { topP: settings.topP }),
        ...(settings.frequencyPenalty !== undefined && { frequencyPenalty: settings.frequencyPenalty }),
        ...(settings.presencePenalty !== undefined && { presencePenalty: settings.presencePenalty }),
        ...(settings.stopSequences !== undefined && { stopSequences: settings.stopSequences }),
        reasoningEffort: EFFORT_TO_OPENAI_REASONING[effort],
      });
    case AIProvider.xai:
      return new OpenAISettings({
        ...(settings.temperature !== undefined && { temperature: settings.temperature }),
        ...(settings.topP !== undefined && { topP: settings.topP }),
        ...(settings.frequencyPenalty !== undefined && { frequencyPenalty: settings.frequencyPenalty }),
        ...(settings.presencePenalty !== undefined && { presencePenalty: settings.presencePenalty }),
        ...(settings.stopSequences !== undefined && { stopSequences: settings.stopSequences }),
      });
    default:
      return undefined;
  }
}

/** Mirrors SBotAIPersonalityModelSettings.resolvedMaxTokens. */
export function resolvedMaxTokens(personality: SBotAIPersonality, defaultValue = 4096): number {
  const maxTokens = personality.modelSettings.maxTokens;
  if (maxTokens === undefined) return defaultValue;
  return Math.min(Math.max(maxTokens, 256), 32_000);
}

function personalitySort(first: SBotAIPersonality, second: SBotAIPersonality): number {
  if (first.sortOrder !== second.sortOrder) return first.sortOrder - second.sortOrder;
  return first.name.localeCompare(second.name, undefined, { sensitivity: "base" });
}

function personalizationContext(user: SBotUser, fields: SBotAIPersonalizationFields): Record<string, string> {
  const context: Record<string, string> = {};
  const put = (key: string, value: string | undefined) => {
    const trimmed = trimmedNonEmpty(value);
    if (trimmed) context[key] = trimmed;
  };
  if (fields.email) put("email", user.email);
  if (fields.displayName) put("displayName", user.displayName ?? `${user.firstName} ${user.lastName}`.trim());
  if (fields.firstName) put("firstName", user.firstName);
  if (fields.lastName) put("lastName", user.lastName);
  if (fields.bio) put("bio", user.bio);
  if (fields.activeWorkspace) put("activeWorkspace", user.activeContext?.displayName);
  return context;
}

function parsePersonality(id: string, value: unknown): SBotAIPersonality | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const data = value as DocumentData;
  const name = str(data.name);
  const systemPrompt = str(data.systemPrompt);
  const providerID = str(data.providerId) ?? str(data.providerID);
  const modelID = str(data.modelId) ?? str(data.modelID);
  if (!name || !systemPrompt || !providerID || !modelID) return undefined;

  return {
    id: str(data.id) ?? id,
    name,
    description: str(data.description),
    avatarURL: str(data.avatarURL),
    systemPrompt,
    providerID,
    modelID,
    modelSettings: parseModelSettings(data.modelSettings),
    personalizationConfig: parsePersonalizationConfig(data.personalizationConfig),
    audioModeConfig: parseAudioModeConfig(data.audioModeConfig),
    isEnabled: bool(data.isEnabled) ?? true,
    isDefault: bool(data.isDefault) ?? false,
    sortOrder: int(data.sortOrder) ?? 0,
  };
}

// Mirrors SBotAIPersonalityModelSettings(data:): when the map exists, absent
// fields stay undefined (no 0.7/0.9 defaults) so reasoning models that reject
// sampling parameters never receive them.
function parseModelSettings(value: unknown): SBotAIPersonalityModelSettings {
  if (typeof value !== "object" || value === null) return {};
  const data = value as DocumentData;
  return {
    temperature: num(data.temperature),
    topP: num(data.topP),
    topK: int(data.topK),
    frequencyPenalty: num(data.frequencyPenalty),
    presencePenalty: num(data.presencePenalty),
    stopSequences: Array.isArray(data.stopSequences) ? data.stopSequences.filter((v: unknown): v is string => typeof v === "string") : undefined,
    maxTokens: int(data.maxTokens),
    maxToolRounds: int(data.maxToolRounds),
  };
}

function parsePersonalizationConfig(value: unknown): SBotAIPersonalizationConfig | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const data = value as DocumentData;
  const fields = typeof data.enabledFields === "object" && data.enabledFields !== null ? (data.enabledFields as DocumentData) : {};
  return {
    isEnabled: bool(data.isEnabled) ?? false,
    enabledFields: {
      email: bool(fields.email) ?? false,
      displayName: bool(fields.displayName) ?? false,
      firstName: bool(fields.firstName) ?? false,
      lastName: bool(fields.lastName) ?? false,
      bio: bool(fields.bio) ?? false,
      activeWorkspace: bool(fields.activeWorkspace) ?? false,
    },
    template: str(data.template),
  };
}

function parseAudioModeConfig(value: unknown): SBotAIAudioModeConfig | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const data = value as DocumentData;
  const providerID = str(data.providerID) ?? str(data.providerId);
  const modelID = str(data.modelID) ?? str(data.modelId);
  const voiceID = str(data.voiceID) ?? str(data.voiceId);
  if (!providerID || !modelID || !voiceID) return undefined;
  return { isEnabled: bool(data.isEnabled) ?? false, providerID, modelID, voiceID };
}

function str(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function bool(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function num(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function int(value: unknown): number | undefined {
  const parsed = num(value);
  return parsed === undefined ? undefined : Math.trunc(parsed);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
